'use client';

import { useState } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Copy01Icon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../../../components/icon-button';
import { MonoDefault, MonoSmall } from '../../../../components/typography';

type ChatCodeBlockProps = {
  code: string;
  language?: string;
  size?: 'sm' | 'md';
  maxHeight?: string;
  tone?: 'default' | 'error';
  prompt?: string;
  copyable?: boolean;
  wrap?: boolean;
};

export function ChatCodeBlock({
  code,
  language,
  size = 'md',
  maxHeight,
  tone = 'default',
  prompt,
  copyable,
  wrap,
}: ChatCodeBlockProps) {
  const tCommon = useTranslations('designSystem.common');
  const [copied, setCopied] = useState(false);
  const Mono = size === 'sm' ? MonoSmall : MonoDefault;
  const text = prompt ? `${prompt} ${code}` : code;

  const copyCode = () => {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box
      position="relative"
      bg="bg.subtle"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      maxH={maxHeight}
      overflow="auto"
      width="100%"
      data-language={language}
      data-tone={tone}
    >
      {copyable ? (
        <HStack justifyContent="flex-end" px={1} pt={1}>
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={tCommon('copy')}
            onClick={copyCode}
          >
            {copied ? <Tick01Icon size={12} /> : <Copy01Icon size={12} />}
          </IconButton>
        </HStack>
      ) : null}
      <Box as="pre" m={0} px={2} py={2} whiteSpace={wrap ? 'pre-wrap' : 'pre'}>
        <Mono as="span" color={tone === 'error' ? 'fg.error' : 'fg.default'} m={0}>
          {text}
        </Mono>
      </Box>
    </Box>
  );
}
